package br.com.vvrefeicoes.portal.feature.shell

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import br.com.vvrefeicoes.portal.core.storage.KeyValueStorage
import br.com.vvrefeicoes.portal.core.storage.createKeyValueStorage
import br.com.vvrefeicoes.portal.core.util.PeriodMode
import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod

internal const val KEY_MODE = "global.period.mode"
internal const val KEY_MONTH = "global.period.month"
internal const val KEY_YEAR = "global.period.year"
internal const val KEY_START = "global.period.start"
internal const val KEY_END = "global.period.end"

class GlobalPeriod(
    private val storage: KeyValueStorage,
    initial: PeriodSelection,
) {
    var selection by mutableStateOf(initial)
        private set

    fun setMode(mode: PeriodMode) {
        selection = selection.copy(mode = mode)
        persist()
    }

    fun selectMonth(month: MonthPeriod) {
        selection = selection.copy(mode = PeriodMode.MONTH, month = month)
        persist()
    }

    fun selectYear(year: Int) {
        selection = selection.copy(mode = PeriodMode.YEAR, year = year)
        persist()
    }

    fun selectCustom(
        start: MonthPeriod,
        end: MonthPeriod,
    ) {
        selection = selection.copy(mode = PeriodMode.CUSTOM, start = start, end = end)
        persist()
    }

    private fun persist() {
        storage.putString(KEY_MODE, selection.mode.name)
        storage.putString(KEY_MONTH, selection.month.id)
        storage.putString(KEY_YEAR, selection.year.toString())
        storage.putString(KEY_START, selection.start.id)
        storage.putString(KEY_END, selection.end.id)
    }
}

@Composable
fun rememberGlobalPeriod(): GlobalPeriod = remember { globalPeriodFrom(createKeyValueStorage()) }

internal fun globalPeriodFrom(storage: KeyValueStorage): GlobalPeriod {
    val fallback = PeriodSelection.current(SystemDateTimeProvider().currentPeriod())
    val initial =
        PeriodSelection(
            mode = storage.getString(KEY_MODE)?.let { raw -> PeriodMode.entries.firstOrNull { it.name == raw } } ?: fallback.mode,
            month = storedMonth(storage, KEY_MONTH) ?: fallback.month,
            year = storage.getString(KEY_YEAR)?.toIntOrNull() ?: fallback.year,
            start = storedMonth(storage, KEY_START) ?: fallback.start,
            end = storedMonth(storage, KEY_END) ?: fallback.end,
        )
    return GlobalPeriod(storage = storage, initial = initial)
}

private fun storedMonth(
    storage: KeyValueStorage,
    key: String,
): MonthPeriod? {
    val parts = storage.getString(key)?.split('-') ?: return null
    if (parts.size != 2) return null
    val year = parts[0].toIntOrNull() ?: return null
    val month = parts[1].toIntOrNull() ?: return null
    return runCatching { MonthPeriod(year, month) }.getOrNull()
}

val LocalGlobalPeriod =
    staticCompositionLocalOf<GlobalPeriod> {
        error("GlobalPeriod não foi fornecido. Envolva a UI com CompositionLocalProvider(LocalGlobalPeriod provides ...).")
    }
